import { Component, type FormEvent, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton, Checkbox, Label } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { type SafetyMessageCreate, type SafetyMessage } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { type FormCreateProps } from "../../utils/form-create-props";
import { createUuid } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormCreateSafetyMessageProps extends FormCreateProps<SafetyMessage> {}

import { store } from "../../store";

@observer
export class FormCreateSafetyMessage extends Component<FormCreateSafetyMessageProps> {
    @observable public accessor body = "";
    @observable public accessor active = false;

    private validationId = createUuid();

    constructor(props: FormCreateSafetyMessageProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositorySafetyMessages.validation.create.updateModel(
            this.validationId,
            this.safetyMessageCreate,
        );
    }

    componentDidMount(): void {
        store.repositorySafetyMessages.validation.create.initializeModel(
            this.validationId,
            this.safetyMessageCreate,
        );
    }

    @computed private get safetyMessageCreate(): SafetyMessageCreate {
        return pick(["body", "active"], this as FormCreateSafetyMessage);
    }

    @action.bound
    private async createSafetyMessage(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const safetyMessage = await store.repositorySafetyMessages.create(this.safetyMessageCreate);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(safetyMessage);
        }
    }

    @action.bound private reset(): void {
        this.body = "";
        this.active = false;
        this.triggerValidation();
    }

    @action.bound private changeActive(
        _event?: FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean,
    ): void {
        this.active = checked!;
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateSafetyMessage.submit.text")}
                validation={store.repositorySafetyMessages.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createSafetyMessage}>
                <Stack
                    horizontal
                    verticalAlign="center"
                    styles={{
                        root: {
                            minWidth: "600px",
                        },
                    }}
                >
                    <Stack.Item tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                        <TextField
                            label={store.i18n.t("formCreateSafetyMessage.body.label")}
                            {...doubleBindString<FormCreateSafetyMessage>(this, "body", () =>
                                this.triggerValidation(),
                            )}
                            required
                            errorMessage={store.i18n.formatFieldValidationState(
                                store.repositorySafetyMessages.validation.create.getFieldValidationState(
                                    this.validationId,
                                    "body",
                                ),
                            )}
                            styles={{
                                root: {
                                    minWidth: "450px",
                                },
                            }}
                        />
                    </Stack.Item>
                    <Stack horizontalAlign="center">
                        <Label>{store.i18n.t("formCreateSafetyMessage.active.label")}</Label>
                        <Checkbox defaultChecked={this.active} onChange={this.changeActive} />
                    </Stack>
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateSafetyMessage.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
